import { SequencingPlatforms } from 'Generated/SequencingPlatform.js'
import { findUnpairedFileNames, hasCorrectFileEnding, hasReadDesignation } from 'Util/SampleFileUtils.js'
import type { DetectedSequencingPlatform } from 'Util/sequencingPlatformDetection.js'
import { consensusSequencingPlatform } from 'Util/sequencingPlatformDetection.js'

export type RejectionReason = 'wrong-extension' | 'no-read-designation' | 'unpaired' | 'platform-mismatch'

/**
 * Everything this needs of a file — satisfied both by an Uppy file and by the plain descriptors the
 * pool's file input builds before they reach Uppy, so the same check runs on both paths.
 */
export type PrevalidatableFile = {
  name?: string | null
  data?: unknown
  meta?: { isMerged?: boolean; detectedSequencingPlatform?: DetectedSequencingPlatform; attachReverseTo?: string } | undefined
}

export type PrevalidationResult<T> = {
  /** Files that may go to the uploader. */
  accepted: T[]
  rejected: { file: T; fileName: string; reason: RejectionReason }[]
  /** One human-readable line per reason that actually occurred, naming the offending files. */
  messages: string[]
}

/** How many file names we spell out before collapsing the rest into "…and N more". */
const MAX_NAMED = 5

export const formatFileNames = (names: string[]): string => {
  const shown = names.slice(0, MAX_NAMED).join(', ')
  return names.length > MAX_NAMED ? `${shown} …and ${names.length - MAX_NAMED} more` : shown
}

const messageFor = (reason: RejectionReason, names: string[], consensus: DetectedSequencingPlatform): string => {
  const count = `${names.length} file${names.length === 1 ? '' : 's'}`
  switch (reason) {
    case 'wrong-extension':
      return `Only .fastq.gz files can be uploaded — skipped ${count}: ${formatFileNames(names)}`
    case 'no-read-designation':
      return (
        `${count} skipped because the read designation is missing: ${formatFileNames(names)}. ` +
        'Files need to be marked as _R1 and _R2, e.g. sample_R1.fastq.gz and sample_R2.fastq.gz. ' +
        'Alternative formats include .ft and .rt as designators.'
      )
    case 'unpaired':
      return (
        `${count} skipped because the matching R1/R2 partner is neither in this selection nor already ` +
        `uploaded to this pool: ${formatFileNames(names)}. Add the partner file(s) as well.`
      )
    case 'platform-mismatch':
      return (
        `${count} skipped because they were not sequenced on the same platform as the rest of this ` +
        `selection (${consensus === 'UNKNOWN' ? 'unknown' : SequencingPlatforms[consensus].label}): ` +
        `${formatFileNames(names)}. An order is processed by one pipeline, so its reads must all come ` +
        'from one sequencing platform.'
      )
  }
}

/**
 * Decides, per file, whether it may be uploaded — and says why not, by name, when it may not.
 *
 * Two things matter here and both were previously wrong:
 *  - Judgement is **per file**: one bad name used to make the whole (possibly 1300-file) selection
 *    bounce, so a single typo cost the user the entire upload.
 *  - Pairing is judged against `alreadyPresent` too — the names already uploaded to the target pool
 *    plus whatever is queued in the uploader. Without that, retrying an upload where only one half of
 *    a pair failed (its partner is already in S3) reported "please upload files in pairs" and dropped
 *    the batch, which is exactly the dead end this validator is supposed to prevent.
 *
 * Sniffing the file for its sequencing platform is NOT done here: it is async, while this stays
 * synchronous and testable without stream mocks. Call sites detect first and hand the verdict in on
 * `meta.detectedSequencingPlatform`; a file whose platform is unknown is judged exactly as before.
 */
export function prevalidateFiles<T extends PrevalidatableFile>(
  files: T[],
  alreadyPresent: readonly string[] = []
): PrevalidationResult<T> {
  const nameOf = (file: T) => file.name ?? (file.data as File | undefined)?.name ?? ''
  const platformOf = (file: T): DetectedSequencingPlatform => file.meta?.detectedSequencingPlatform ?? 'UNKNOWN'
  /* a file uploaded as one sample's missing R2 is paired by that choice, whatever its name says */
  const singleEnded = (file: T) => file.meta?.isMerged || platformOf(file) === 'ONT' || !!file.meta?.attachReverseTo
  const rejected: PrevalidationResult<T>['rejected'] = []
  const reject = (file: T, reason: RejectionReason) => rejected.push({ file, fileName: nameOf(file), reason })

  const readable = files.filter((file) => {
    if (!hasCorrectFileEnding(nameOf(file))) {
      reject(file, 'wrong-extension')
      return false
    }
    return true
  })

  // Detected-to-detected, per the platform design: files must agree with each other whatever the
  // order declares, or an override would silently switch the guard off. Undetected files abstain.
  const consensus = consensusSequencingPlatform(readable.map(platformOf))
  const agreeing = readable.filter((file) => {
    const platform = platformOf(file)
    if (platform !== 'UNKNOWN' && consensus !== 'UNKNOWN' && platform !== consensus) {
      reject(file, 'platform-mismatch')
      return false
    }
    return true
  })

  const remaining = agreeing.filter((file) => {
    /* single-ended reads — ONT, or merged samples — carry no read designation and need no partner */
    if (!singleEnded(file) && !hasReadDesignation(nameOf(file))) {
      reject(file, 'no-read-designation')
      return false
    }
    return true
  })

  const needingPairs = remaining.filter((file) => !singleEnded(file))
  const unpaired = new Set(findUnpairedFileNames(needingPairs.map(nameOf), alreadyPresent))
  const accepted = remaining.filter((file) => {
    if (!singleEnded(file) && unpaired.has(nameOf(file))) {
      reject(file, 'unpaired')
      return false
    }
    return true
  })

  const messages = (['wrong-extension', 'platform-mismatch', 'no-read-designation', 'unpaired'] as const)
    .map((reason) => ({ reason, names: rejected.filter((r) => r.reason === reason).map((r) => r.fileName) }))
    .filter(({ names }) => names.length > 0)
    .map(({ reason, names }) => messageFor(reason, names, consensus))

  return { accepted, rejected, messages }
}
